import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Facebook, Instagram, Youtube, Twitter, Linkedin, Globe, Mail, Phone } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../Libs/firebase-config.mjs';

// Icon mapping — mirrors FooterEditor's AVAILABLE_ICONS
const ICON_MAP = {
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Globe,
  Mail,
  Phone,
};

/**
 * Normalise any URL the editor might store:
 *  - bare '#'            → keep as '#'  (no-op placeholder)
 *  - 'facebook.com/...'  → 'https://facebook.com/...'
 *  - 'https://...'       → unchanged
 *  - empty / undefined   → '#'
 */
const normaliseUrl = (url) => {
  if (!url || url.trim() === '' || url.trim() === '#') return '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^tel:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const DEFAULT_DATA = {
  brandName: 'Rev. Peter Kyengo',
  brandDescription:
    'Founder and Lead Minister of Faith Life Evangelistic Church (FLEC). Proclaiming Christ, equipping saints, and evangelizing the world across five Kenyan counties through transformative ministry and leadership.',
  location: 'Nairobi, Kenya',
  scripture: '"For to me, to live is Christ and to die is gain." - Philippians 1:21',
  footerNote: 'Built with faith & love',
  email: 'pastor@kyengo.co.ke',
  phone: '+254 712 345 678',
  socialLinks: [
    { icon: 'Facebook', name: 'Facebook', url: '#' },
    { icon: 'Instagram', name: 'Instagram', url: '#' },
    { icon: 'Youtube', name: 'YouTube', url: '#' },
    { icon: 'Twitter', name: 'Twitter', url: '#' },
  ],
  quickLinks: [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'calling' },
    { label: 'Journey', id: 'journey' },
    { label: 'Ministry', id: 'ministry' },
    { label: 'Album', id: 'album' },
    { label: 'Books', id: 'books' },
    { label: 'Contact', id: 'connect' },
  ],
};

const Footer = ({ scrollToSection, darkMode }) => {
  const currentYear = new Date().getFullYear();
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    try {
      const docRef = doc(db, 'footerSection', 'content');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setFooterData(docSnap.data());
      } else {
        setFooterData(DEFAULT_DATA);
      }
    } catch (err) {
      console.error('Error loading footer data:', err);
      setFooterData(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  };

  // While loading, render a minimal placeholder to avoid layout shift
  if (loading) {
    return (
      <footer
        className={`${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'} py-16 px-8 border-t transition-colors duration-300`}
        style={{ minHeight: '200px' }}
      />
    );
  }

  const data = footerData || DEFAULT_DATA;

  return (
    <footer
      className={`${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'} py-16 px-8 border-t transition-colors duration-300`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">

          {/* Brand Section */}
          <div className="md:col-span-2">
            <motion.h3
              className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {data.brandName}
            </motion.h3>

            <motion.p
              className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} mb-4 leading-relaxed`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {data.brandDescription}
            </motion.p>

            <motion.p
              className={`${darkMode ? 'text-slate-400' : 'text-slate-600'}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {data.location}
            </motion.p>
          </div>

          {/* Quick Links */}
          <div>
            <motion.h4
              className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Quick Links
            </motion.h4>
            <div className="space-y-2">
              {data.quickLinks && data.quickLinks.map((link, index) => (
                <motion.button
                  key={link.id || index}
                  onClick={() => scrollToSection && scrollToSection(link.id)}
                  className={`block ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors text-left`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 5 }}
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Connect / Social */}
          <div>
            <motion.h4
              className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Connect With Us
            </motion.h4>

            <div className="flex flex-col gap-4">
              {data.socialLinks && data.socialLinks.map((social, index) => {
                const IconComponent = ICON_MAP[social.icon];
                return (
                  <motion.a
                    key={social.name || index}
                    href={normaliseUrl(social.url)}
                    target={normaliseUrl(social.url) !== '#' ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors group`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5 }}
                  >
                    <div
                      className={`p-2 rounded-lg ${darkMode ? 'bg-slate-800 group-hover:bg-slate-700' : 'bg-slate-100 group-hover:bg-slate-200'} transition-colors`}
                    >
                      {IconComponent ? (
                        <IconComponent className="w-5 h-5" />
                      ) : (
                        <Globe className="w-5 h-5" />
                      )}
                    </div>
                    <span>{social.name}</span>
                  </motion.a>
                );
              })}
            </div>

            {/* Contact Info */}
            <motion.div
              className="mt-6 space-y-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {data.email && (
                <a
                  href={`mailto:${data.email}`}
                  className={`flex items-center gap-2 text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  {data.email}
                </a>
              )}
              {data.phone && (
                <a
                  href={`tel:${data.phone.replace(/\s+/g, '')}`}
                  className={`flex items-center gap-2 text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  {data.phone}
                </a>
              )}
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className={`${darkMode ? 'border-slate-800' : 'border-slate-200'} border-t pt-8`}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <motion.p
              className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-sm flex items-center gap-2`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              © {currentYear} {data.brandName}. All rights reserved.
              <Heart className="w-4 h-4 text-red-500" />
            </motion.p>

            {data.scripture && (
              <motion.p
                className={`${darkMode ? 'text-slate-500' : 'text-slate-500'} text-sm italic text-center`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                {data.scripture}
              </motion.p>
            )}

            {data.footerNote && (
              <motion.div
                className={`${darkMode ? 'text-slate-500' : 'text-slate-500'} text-sm`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                {data.footerNote}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;